import { filterAndSortAvailableTimes } from "api/api-hooks/available-times-utils";
import { AvailableTimesSection, AvailableTimesUnit } from "types/available-times";
import { WebBooking } from "types/web-booking";
import { WebRule, WebRuleBookingType } from "types/web-rule";
import { AvailabilityState, getAvailabilityState } from "utils/availability-utils";

function isExperienceBooking(booking: WebBooking): boolean {
    return Boolean(
        booking.webBookingRules?.some(
            (rule) =>
                rule.bookingType === WebRuleBookingType.Experience || Boolean(rule.experienceId)
        )
    );
}

function getExperienceEditSections(
    availableTimesData: AvailableTimesUnit,
    booking: WebBooking,
    guests: number,
    isBookingRequest: boolean
): AvailableTimesSection[] {
    const bookedRules = booking.webBookingRules ?? [];
    const bookedExperienceIds = new Set(
        bookedRules
            .map((rule) => rule.experienceId)
            .filter((experienceId): experienceId is number => Boolean(experienceId))
    );
    const bookedSectionIds = new Set(bookedRules.map((rule) => rule.sectionId));
    const bookedRuleIds = new Set(bookedRules.map((rule) => rule.id));

    return availableTimesData.experiences
        .filter((experience) => {
            if (bookedExperienceIds.size > 0) {
                // Matched on parentId as well as id: an availableTimes experience may be a child
                // of the one the booking refers to, the same rule experience-view-utils uses.
                return (
                    bookedExperienceIds.has(experience.id) ||
                    (experience.parentId !== undefined &&
                        bookedExperienceIds.has(experience.parentId))
                );
            }

            return experience.activities.some((activity) =>
                activity.timeSets.some((rule) => bookedRuleIds.has(rule.id))
            );
        })
        .flatMap((experience) =>
            experience.activities
                .filter(
                    (activity) =>
                        bookedSectionIds.size === 0 || bookedSectionIds.has(activity.sectionId)
                )
                .map((activity) => {
                    const timeSets = activity.timeSets
                        .filter((rule) => rule.bookingType === WebRuleBookingType.Experience)
                        .map(
                            (rule) =>
                                new WebRule({
                                    ...rule,
                                    times: rule.times.filter((time) => !time.timeExpired),
                                })
                        )
                        .filter((rule) => rule.isTextRow || rule.times.length > 0);

                    return new AvailableTimesSection({
                        id: activity.sectionId,
                        name: activity.sectionName,
                        sortOrder: activity.sortOrder,
                        timeSets,
                        availabilityState: isBookingRequest
                            ? AvailabilityState.Available
                            : getAvailabilityState(timeSets, guests),
                    });
                })
        )
        .filter((section) => section.timeSets.length > 0)
        .sort((a, b) => a.sortOrder - b.sortOrder);
}

function getEditBookingAvailableTimes(
    availableTimesData: AvailableTimesUnit,
    booking: WebBooking,
    guests: number,
    isBookingRequest: boolean
): AvailableTimesSection[] {
    if (isExperienceBooking(booking)) {
        return getExperienceEditSections(availableTimesData, booking, guests, isBookingRequest);
    }

    return filterAndSortAvailableTimes(
        availableTimesData,
        isBookingRequest ? WebRuleBookingType.Request : WebRuleBookingType.Booking,
        guests,
        isBookingRequest
    );
}

export { getEditBookingAvailableTimes };
